import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Button, Divider, IconButton, Step, StepLabel, Stepper } from "@mui/material";
import axios from "../utils/api";
import { subscriptionAdminApi } from "../subscriptionAdmin/api";
import { planBasePrice, planFinalPrice } from "../subscriptionAdmin/planUtils";
import { formatInr } from "../subscriptionAdmin/razorpay";

/** Main catalog plans only — matches userController.resolveRegistrationPlan */
function normalizeMainCatalogPlans(plans) {
  return (plans || []).filter(
    (p) => p.planCategory !== "addon" && p.isAddOn !== true
  );
}

function validateSubscriptionPlanSelection(plans, catalog, newErrors) {
  if (!plans?.length) {
    newErrors.usersubscriptionplans =
      "Select one main catalog plan. Entitlement activates via contract + payment-setup, not at register.";
    return;
  }
  if (plans.length > 1) {
    newErrors.usersubscriptionplans = "Only one main plan per user at registration.";
    return;
  }
  const planId = plans[0].subscriptionPlanId || plans[0].planId;
  if (!planId) {
    newErrors.subscriptionPlan0 = "Select a main catalog plan.";
    return;
  }
  const match = catalog.find((p) => String(p._id) === String(planId));
  if (!match) {
    newErrors.subscriptionPlan0 = "Plan not found in catalog.";
  } else if (match.planCategory === "addon") {
    newErrors.subscriptionPlan0 =
      "Addon plans cannot be assigned at registration. Use contract flow after activation.";
  }
}

export default function CreateUser() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [primaryContactName, setPrimaryContactName] = useState("");
  const [primaryContactEmail, setPrimaryContactEmail] = useState("");
  const [primaryContactPhoneNumber, setPrimaryContactPhoneNumber] = useState("");
  const [purchaseType, setPurchaseType] = useState("purchaseuser");
  const [allowUserAppOnWeb, setAllowUserAppOnWeb] = useState(false);
  const [billingGstin, setBillingGstin] = useState("");
  const [billingStateCode, setBillingStateCode] = useState("");
  const [billingLegalName, setBillingLegalName] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [usersubscriptionplans, setUsersubscriptionplans] = useState([]);
  const [availableSubscriptionPlans, setAvailableSubscriptionPlans] = useState([]);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarIsError, setSnackbarIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [locations, setLocations] = useState([
    {
      name: "",
      address: { street: "", town: "", city: "", state: "", pinCode: "" },
      subLocations: [{ name: "" }],
    },
  ]);
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    const from = location.state?.from;
    if (typeof from === "string" && from.startsWith("/")) {
      navigate(from);
      return;
    }
    navigate(-1);
  };

  const steps = useMemo(
    () => [
      "Contact Information",
      "Primary Contact",
      "Purchase & Subscription",
      "Location",
      "Overview",
    ],
    []
  );

  // Fetch main catalog plans (subscription-admin, fallback legacy)
  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      try {
        const data = await subscriptionAdminApi.listPlans({
          planCategory: "main",
          status: "active",
        });
        const plans = normalizeMainCatalogPlans(Array.isArray(data) ? data : []);
        if (plans.length) {
          setAvailableSubscriptionPlans(plans);
          return;
        }
      } catch (err) {
        console.warn("subscription-admin plans-list unavailable", err);
        setAvailableSubscriptionPlans([]);
      }
    };

    fetchSubscriptionPlans();
  }, []);

  // Handle subscription plan management - only one plan allowed
  const addSubscriptionPlan = () => {
    // Only allow adding if no plans exist (maximum 1 plan)
    if (usersubscriptionplans.length === 0) {
      setUsersubscriptionplans([
        {
          subscriptionPlanId: "",
          planpaymentStatus: "pending",
        },
      ]);
    }
  };

  const removeSubscriptionPlan = (index) => {
    const updatedPlans = usersubscriptionplans.filter((_, i) => i !== index);
    setUsersubscriptionplans(updatedPlans);
  };

  const handlePurchaseTypeChange = (value) => {
    setPurchaseType(value);
    if (value === "subscriptionuser" && usersubscriptionplans.length === 0) {
      setUsersubscriptionplans([{ subscriptionPlanId: "", planpaymentStatus: "pending" }]);
    }
    if (value === "purchaseuser") {
      setUsersubscriptionplans([]);
    }
  };

  const handleSubscriptionPlanChange = (index, planId) => {
    const match = availableSubscriptionPlans.find((p) => String(p._id) === String(planId));
    const updatedPlans = [...usersubscriptionplans];
    updatedPlans[index] = {
      subscriptionPlanId: planId || "",
      planId: planId || "",
      planName: match?.planName || "",
      planpaymentStatus: "pending",
    };
    setUsersubscriptionplans(updatedPlans);
  };

  const selectedCatalogPlan = (planEntry) => {
    const id = planEntry?.subscriptionPlanId || planEntry?.planId;
    return availableSubscriptionPlans.find((p) => String(p._id) === String(id));
  };

  const handleLocationChange = (index, event) => {
    const newLocations = [...locations];
    newLocations[index][event.target.name] = event.target.value;
    setLocations(newLocations);
  };

  const handleAddressChange = (index, event) => {
    const newLocations = [...locations];
    newLocations[index].address[event.target.name] = event.target.value;
    setLocations(newLocations);
  };

  const handleSubLocationChange = (locationIndex, subLocationIndex, event) => {
    const newLocations = [...locations];
    newLocations[locationIndex].subLocations[subLocationIndex][
      event.target.name
    ] = event.target.value;
    setLocations(newLocations);
  };

  const addLocation = () => {
    setLocations([
      ...locations,
      {
        name: "",
        address: { street: "", town: "", city: "", state: "", pinCode: "" },
        subLocations: [{ name: "" }],
      },
    ]);
  };

  const addSubLocation = (locationIndex) => {
    const newLocations = [...locations];
    newLocations[locationIndex].subLocations.push({ name: "" });
    setLocations(newLocations);
  };

  const removeLocation = (index) => {
    const newLocations = locations.filter((_, locIndex) => locIndex !== index);
    setLocations(newLocations);
  };

  const removeSubLocation = (locationIndex, subLocationIndex) => {
    const newLocations = [...locations];
    newLocations[locationIndex].subLocations = newLocations[
      locationIndex
    ].subLocations.filter((_, subIndex) => subIndex !== subLocationIndex);
    setLocations(newLocations);
  };

  const validateFields = () => {
    const newErrors = {};
    if (!name) newErrors.name = "Name is required";
    if (!email || !/\S+@\S+\.\S+/.test(email))
      newErrors.email = "Valid email is required";
    if (!phoneNumber || !/^\d{10}$/.test(phoneNumber))
      newErrors.phoneNumber = "Valid phone number is required (10 digits)";
    if (!organizationName)
      newErrors.organizationName = "Organization name is required";
    if (!password || password.length < 8)
      newErrors.password = "Password must be at least 8 characters long";
    if (!primaryContactName)
      newErrors.primaryContactName = "Primary contact name is required";
    if (!primaryContactEmail || !/\S+@\S+\.\S+/.test(primaryContactEmail))
      newErrors.primaryContactEmail = "Valid primary contact email is required";
    else if (email === primaryContactEmail)
      newErrors.primaryContactEmail =
        "Primary contact email must be different from the email";
    if (
      !primaryContactPhoneNumber ||
      !/^\d{10}$/.test(primaryContactPhoneNumber)
    )
      newErrors.primaryContactPhoneNumber =
        "Valid primary contact phone number is required (10 digits)";
    else if (phoneNumber === primaryContactPhoneNumber)
      newErrors.primaryContactPhoneNumber =
        "Primary contact Number must be different from the PhoneNumber";
    locations.forEach((location, index) => {
      if (!location.name)
        newErrors[`locationName${index}`] = `Location ${index + 1} name is required`;
      if (!location.address.street)
        newErrors[`locationAddressstreet${index}`] = `Location ${index + 1} street address is required`;
      if (!location.address.town)
        newErrors[`locationAddresstown${index}`] = `Location ${index + 1} town address is required`;
      if (!location.address.city)
        newErrors[`locationAddresscity${index}`] = `Location ${index + 1} city address is required`;
      if (!location.address.state)
        newErrors[`locationAddressstate${index}`] = `Location ${index + 1} state address is required`;
      if (!location.address.pinCode)
        newErrors[`locationAddresspinCode${index}`] = `Location ${index + 1} pin code is required`;
      location.subLocations.forEach((subLocation, subIndex) => {
        if (!subLocation.name)
          newErrors[`subLocationName${index}-${subIndex}`] = `Sub-Location ${subIndex + 1} name is required`;
      });
    });
    // Validate purchaseType and subscription fields
    if (!purchaseType) newErrors.purchaseType = "Purchase type is required";
    if (purchaseType === "subscriptionuser") {
      validateSubscriptionPlanSelection(
        usersubscriptionplans,
        availableSubscriptionPlans,
        newErrors
      );
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep = (stepIndex) => {
    const newErrors = {};

    if (stepIndex === 0) {
      if (!name) newErrors.name = "Name is required";
      if (!email || !/\S+@\S+\.\S+/.test(email))
        newErrors.email = "Valid email is required";
      if (!phoneNumber || !/^\d{10}$/.test(phoneNumber))
        newErrors.phoneNumber = "Valid phone number is required (10 digits)";
      if (!organizationName)
        newErrors.organizationName = "Organization name is required";
      if (!password || password.length < 8)
        newErrors.password = "Password must be at least 8 characters long";
    }

    if (stepIndex === 1) {
      if (!primaryContactName)
        newErrors.primaryContactName = "Primary contact name is required";
      if (!primaryContactEmail || !/\S+@\S+\.\S+/.test(primaryContactEmail))
        newErrors.primaryContactEmail = "Valid primary contact email is required";
      else if (email === primaryContactEmail)
        newErrors.primaryContactEmail =
          "Primary contact email must be different from the email";

      if (
        !primaryContactPhoneNumber ||
        !/^\d{10}$/.test(primaryContactPhoneNumber)
      )
        newErrors.primaryContactPhoneNumber =
          "Valid primary contact phone number is required (10 digits)";
      else if (phoneNumber === primaryContactPhoneNumber)
        newErrors.primaryContactPhoneNumber =
          "Primary contact Number must be different from the PhoneNumber";
    }

    if (stepIndex === 2) {
      if (!purchaseType) newErrors.purchaseType = "Purchase type is required";
      if (purchaseType === "subscriptionuser") {
        validateSubscriptionPlanSelection(
          usersubscriptionplans,
          availableSubscriptionPlans,
          newErrors
        );
      }
    }

    if (stepIndex === 3) {
      locations.forEach((location, index) => {
        if (!location.name)
          newErrors[`locationName${index}`] = `Location ${index + 1} name is required`;
        if (!location.address.street)
          newErrors[`locationAddressstreet${index}`] = `Location ${index + 1} street address is required`;
        if (!location.address.town)
          newErrors[`locationAddresstown${index}`] = `Location ${index + 1} town address is required`;
        if (!location.address.city)
          newErrors[`locationAddresscity${index}`] = `Location ${index + 1} city address is required`;
        if (!location.address.state)
          newErrors[`locationAddressstate${index}`] = `Location ${index + 1} state address is required`;
        if (!location.address.pinCode)
          newErrors[`locationAddresspinCode${index}`] = `Location ${index + 1} pin code is required`;
        location.subLocations.forEach((subLocation, subIndex) => {
          if (!subLocation.name)
            newErrors[`subLocationName${index}-${subIndex}`] = `Sub-Location ${subIndex + 1} name is required`;
        });
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // Prevent multiple submissions
    if (isSubmitting) return;

    if (!validateFields()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        name,
        email,
        phoneNumber,
        organizationName,
        password,
        primaryContact: {
          name: primaryContactName,
          email: primaryContactEmail,
          phoneNumber: primaryContactPhoneNumber,
        },
        locations,
        purchaseType,
        allowUserAppOnWeb: allowUserAppOnWeb === true,
      };
      if (purchaseType === "subscriptionuser") {
        const planId =
          usersubscriptionplans[0]?.subscriptionPlanId ||
          usersubscriptionplans[0]?.planId;
        payload.subscriptionPlanId = planId;
        payload.usersubscriptionplans = [{ subscriptionPlanId: planId }];
        const billingProfile = {
          gstin: billingGstin.trim() || undefined,
          stateCode: billingStateCode.trim() || undefined,
          legalName: billingLegalName.trim() || undefined,
          address: billingAddress.trim() || undefined,
        };
        if (Object.values(billingProfile).some(Boolean)) {
          payload.billingProfile = billingProfile;
        }
      }
      const response = await axios.post("/register-user", payload);
      const data = response.data || {};
      setMessage("User created successfully.");
      setSnackbarIsError(false);
      setSnackbarOpen(true);
      setEmail("");
      setPassword("");
      setName("");
      setPhoneNumber("");
      setOrganizationName("");
      setPrimaryContactName("");
      setPrimaryContactEmail("");
      setPrimaryContactPhoneNumber("");
      setPurchaseType("purchaseuser");
      setAllowUserAppOnWeb(false);
      setUsersubscriptionplans([]);
      setBillingGstin("");
      setBillingStateCode("");
      setBillingLegalName("");
      setBillingAddress("");
      setLocations([
        {
          name: "",
          address: { street: "", town: "", city: "", state: "", pinCode: "" },
          subLocations: [{ name: "" }],
        },
      ]);
      const redirectUserId = data.userId;
      const isSubscription = data.purchaseType === "subscriptionuser" || purchaseType === "subscriptionuser";
      setTimeout(() => {
        if (isSubscription && redirectUserId) {
          navigate("/admin-subscriptions/contracts", {
            state: { prefillUserId: redirectUserId },
          });
        } else {
          navigate("/users-list");
        }
      }, 2500);
    } catch (error) {
      console.error(error);
      setMessage(
        error.response?.data?.message || "An error occurred during registration"
      );
      setSnackbarIsError(true);
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const handleNext = () => {
    if (activeStep >= steps.length - 1) return;
    if (!validateStep(activeStep)) return;
    setActiveStep((s) => Math.min(steps.length - 1, s + 1));
  };

  const handlePrevious = () => {
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const renderContactStep = () => (
    <fieldset style={{ ...styles.fieldset, ...styles.accentContact }}>
      <legend style={styles.legend}>Contact Information</legend>

      <div style={styles.formGroup}>
        <label style={styles.label}>Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={styles.input}
          placeholder="Enter full name"
        />
        {errors.name && <span style={styles.error}>{errors.name}</span>}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={styles.input}
          placeholder="Enter email address"
        />
        {errors.email && <span style={styles.error}>{errors.email}</span>}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Phone Number</label>
        <input
          type="text"
          value={phoneNumber}
          onChange={(e) => {
            const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10);
            setPhoneNumber(onlyDigits);
          }}
          style={styles.input}
          placeholder="Enter phone number"
        />
        {errors.phoneNumber && <span style={styles.error}>{errors.phoneNumber}</span>}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Organization Name</label>
        <input
          type="text"
          value={organizationName}
          onChange={(e) => setOrganizationName(e.target.value)}
          style={styles.input}
          placeholder="Enter organization name"
        />
        {errors.organizationName && (
          <span style={styles.error}>{errors.organizationName}</span>
        )}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Password</label>
        <div style={styles.passwordContainer}>
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.passwordInput}
            placeholder="Enter password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={styles.showPasswordButton}
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>
        {errors.password && <span style={styles.error}>{errors.password}</span>}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={allowUserAppOnWeb}
            onChange={(e) => setAllowUserAppOnWeb(e.target.checked)}
          />
          Allow user app on web
        </label>
      </div>
    </fieldset>
  );

  const renderPrimaryContactStep = () => (
    <fieldset style={{ ...styles.fieldset, ...styles.accentPrimary }}>
      <legend style={styles.legend}>Primary Contact</legend>

      <div style={styles.formGroup}>
        <label style={styles.label}>Contact Name</label>
        <input
          type="text"
          value={primaryContactName}
          onChange={(e) => setPrimaryContactName(e.target.value)}
          style={styles.input}
          placeholder="Enter contact name"
        />
        {errors.primaryContactName && (
          <span style={styles.error}>{errors.primaryContactName}</span>
        )}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Contact Email</label>
        <input
          type="email"
          value={primaryContactEmail}
          onChange={(e) => setPrimaryContactEmail(e.target.value)}
          style={styles.input}
          placeholder="Enter contact email"
        />
        {errors.primaryContactEmail && (
          <span style={styles.error}>{errors.primaryContactEmail}</span>
        )}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Contact Phone Number</label>
        <input
          type="text"
          value={primaryContactPhoneNumber}
          onChange={(e) => {
            const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10);
            setPrimaryContactPhoneNumber(onlyDigits);
          }}
          style={styles.input}
          placeholder="Enter contact phone number"
        />
        {errors.primaryContactPhoneNumber && (
          <span style={styles.error}>{errors.primaryContactPhoneNumber}</span>
        )}
      </div>
    </fieldset>
  );

  const renderPurchaseStep = () => (
    <fieldset style={{ ...styles.fieldset, ...styles.accentPurchase }}>
      <legend style={styles.legend}>Purchase & Subscription</legend>
      <div style={styles.formGroup}>
        <label style={styles.label}>Purchase Type</label>
        <select
          value={purchaseType}
          onChange={(e) => handlePurchaseTypeChange(e.target.value)}
          style={styles.input}
        >
          <option value="purchaseuser">Purchase User</option>
          <option value="subscriptionuser">Subscription User</option>
        </select>
        {errors.purchaseType && <span style={styles.error}>{errors.purchaseType}</span>}
      </div>
      {purchaseType === "subscriptionuser" && (
        <div>
          <p style={styles.infoText}>
            Registers a <strong>pending</strong> plan snapshot on the user. Activation happens later via{" "}
            <strong>subscription-admin contracts</strong> and <strong>payment-setup</strong> — not at register.
            Addon plans are not allowed here.
          </p>

          <div style={styles.formGroup}>
            <label style={styles.label}>Main catalog plan</label>
            {usersubscriptionplans.length === 0 && (
              <button type="button" onClick={addSubscriptionPlan} style={styles.addButton}>
                + Select plan
              </button>
            )}
            {errors.usersubscriptionplans && (
              <span style={styles.error}>{errors.usersubscriptionplans}</span>
            )}
          </div>

          {usersubscriptionplans.map((plan, index) => {
            const catalog = selectedCatalogPlan(plan);
            return (
              <div key={index} style={styles.subscriptionPlanCard}>
                <h4 style={styles.subscriptionPlanTitle}>Catalog plan (snapshot)</h4>

                <div style={styles.formGroup}>
                  <select
                    value={plan.subscriptionPlanId || plan.planId || ""}
                    onChange={(e) => handleSubscriptionPlanChange(index, e.target.value)}
                    style={styles.input}
                  >
                    <option value="">Select main plan…</option>
                    {availableSubscriptionPlans.length === 0 ? (
                      <option value="" disabled>
                        No active main plans — create one in Admin Subscriptions → Plans
                      </option>
                    ) : (
                      availableSubscriptionPlans.map((availablePlan) => (
                        <option key={availablePlan._id} value={availablePlan._id}>
                          {availablePlan.planName}
                          {availablePlan.planType ? ` (${availablePlan.planType})` : ""}
                          {planFinalPrice(availablePlan) != null
                            ? ` — ${formatInr(planFinalPrice(availablePlan))}`
                            : ""}
                        </option>
                      ))
                    )}
                  </select>
                  {(errors[`subscriptionPlan${index}`] || errors.subscriptionPlan0) && (
                    <span style={styles.error}>
                      {errors[`subscriptionPlan${index}`] || errors.subscriptionPlan0}
                    </span>
                  )}
                </div>

                {catalog && (() => {
                  const base = planBasePrice(catalog);
                  const final = planFinalPrice(catalog);
                  const discountPct = Number(catalog?.pricing?.discount?.percent) || 0;
                  const discountOff =
                    base != null && final != null && base > final ? base - final : 0;
                  return (
                    <div style={styles.planPreview}>
                      <div style={styles.planPricing}>
                        <div>
                          <strong>Base price:</strong> {formatInr(base)}
                        </div>
                        <div>
                          <strong>Discount:</strong>{" "}
                          {discountPct > 0
                            ? `${discountPct}%${
                                discountOff > 0
                                  ? ` (${formatInr(discountOff)} off)`
                                  : ""
                              }`
                            : "None"}
                        </div>
                        <div style={styles.planPricingFinal}>
                          <strong>Final price:</strong> {formatInr(final)}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <button
                  type="button"
                  onClick={() => removeSubscriptionPlan(index)}
                  style={styles.removeButton}
                >
                  Clear plan
                </button>
              </div>
            );
          })}

          <div style={styles.billingSection}>
            <h4 style={styles.subtitle}>Billing profile (GST / invoices)</h4>
            <p style={styles.infoText}>
              Optional. Used by subscription-admin contracts and payment for CGST/SGST vs IGST.
            </p>
            <div style={styles.formGroup}>
              <label style={styles.label}>GSTIN</label>
              <input
                type="text"
                value={billingGstin}
                onChange={(e) => setBillingGstin(e.target.value)}
                style={styles.input}
                placeholder="29AAAAA0000A1Z5"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>State code</label>
              <input
                type="text"
                value={billingStateCode}
                onChange={(e) => setBillingStateCode(e.target.value)}
                style={styles.input}
                placeholder="29"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Legal name</label>
              <input
                type="text"
                value={billingLegalName}
                onChange={(e) => setBillingLegalName(e.target.value)}
                style={styles.input}
                placeholder="Registered business name"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Billing address</label>
              <textarea
                value={billingAddress}
                onChange={(e) => setBillingAddress(e.target.value)}
                style={{ ...styles.input, minHeight: 72 }}
                placeholder="Address for invoices"
              />
            </div>
          </div>
        </div>
      )}
    </fieldset>
  );

  const renderLocationStep = () => (
    <fieldset style={{ ...styles.fieldset, ...styles.accentLocation }}>
      <legend style={styles.legend}>Locations</legend>
      {locations.map((location, index) => (
        <div key={index} style={styles.locationCard}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Location Name</label>
            <input
              type="text"
              name="name"
              value={location.name}
              onChange={(e) => handleLocationChange(index, e)}
              style={styles.input}
              placeholder="Enter location name"
            />
            {errors[`locationName${index}`] && (
              <span style={styles.error}>{errors[`locationName${index}`]}</span>
            )}
          </div>
          <div style={styles.addressSection}>
            <h4 style={styles.subtitle}>Address</h4>
            {["street", "town", "city", "state", "pinCode"].map((field) => (
              <div style={styles.formGroup} key={field}>
                <label style={styles.label}>
                  {field.charAt(0).toUpperCase() + field.slice(1)}
                </label>
                <input
                  type="text"
                  name={field}
                  value={location.address[field]}
                  onChange={(e) => handleAddressChange(index, e)}
                  style={styles.input}
                  placeholder={`Enter ${field}`}
                />
                {errors[`locationAddress${field}${index}`] && (
                  <span style={styles.error}>{errors[`locationAddress${field}${index}`]}</span>
                )}
              </div>
            ))}
          </div>
          <div style={styles.subLocationsSection}>
            <h4 style={styles.subtitle}>Sub-Locations</h4>
            {location.subLocations.map((subLocation, subIndex) => (
              <div key={subIndex} style={styles.subLocationGroup}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Sub-Location {subIndex + 1}</label>
                  <input
                    type="text"
                    name="name"
                    value={subLocation.name}
                    onChange={(e) => handleSubLocationChange(index, subIndex, e)}
                    style={styles.input}
                    placeholder="Enter sub-location name"
                  />
                  {errors[`subLocationName${index}-${subIndex}`] && (
                    <span style={styles.error}>{errors[`subLocationName${index}-${subIndex}`]}</span>
                  )}
                </div>
                {subIndex > 0 && (
                  <button
                    type="button"
                    onClick={() => removeSubLocation(index, subIndex)}
                    style={styles.removeButton}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => addSubLocation(index)} style={styles.addButton}>
              + Add Sub-Location
            </button>
          </div>
          {locations.length > 1 && (
            <button
              type="button"
              onClick={() => removeLocation(index)}
              style={styles.removeLocationButton}
            >
              Remove Location
            </button>
          )}
        </div>
      ))}
      <button type="button" onClick={addLocation} style={styles.addButton}>
        + Add Another Location
      </button>
    </fieldset>
  );

  const renderOverviewStep = () => (
    <div style={styles.overviewCard}>
      <h3 style={styles.overviewTitle}>Overview</h3>
      <Divider style={styles.divider} />

      <div style={styles.overviewSection}>
        <div style={styles.overviewSectionTitle}>Contact Information</div>
        <div style={styles.overviewGrid}>
          <div style={styles.overviewItem}>
            <div style={styles.overviewLabel}>Name</div>
            <div style={styles.overviewValue}>{name || "-"}</div>
          </div>
          <div style={styles.overviewItem}>
            <div style={styles.overviewLabel}>Email</div>
            <div style={styles.overviewValue}>{email || "-"}</div>
          </div>
          <div style={styles.overviewItem}>
            <div style={styles.overviewLabel}>Phone Number</div>
            <div style={styles.overviewValue}>{phoneNumber || "-"}</div>
          </div>
          <div style={styles.overviewItem}>
            <div style={styles.overviewLabel}>Organization Name</div>
            <div style={styles.overviewValue}>{organizationName || "-"}</div>
          </div>
          <div style={styles.overviewItem}>
            <div style={styles.overviewLabel}>Password</div>
            <div style={styles.overviewValue}>••••••••</div>
          </div>
        </div>
      </div>

      <div style={styles.overviewSection}>
        <div style={styles.overviewSectionTitle}>Primary Contact</div>
        <div style={styles.overviewGrid}>
          <div style={styles.overviewItem}>
            <div style={styles.overviewLabel}>Contact Name</div>
            <div style={styles.overviewValue}>{primaryContactName || "-"}</div>
          </div>
          <div style={styles.overviewItem}>
            <div style={styles.overviewLabel}>Contact Email</div>
            <div style={styles.overviewValue}>{primaryContactEmail || "-"}</div>
          </div>
          <div style={styles.overviewItem}>
            <div style={styles.overviewLabel}>Contact Phone</div>
            <div style={styles.overviewValue}>{primaryContactPhoneNumber || "-"}</div>
          </div>
        </div>
      </div>

      <div style={styles.overviewSection}>
        <div style={styles.overviewSectionTitle}>Purchase & Subscription</div>
        <div style={styles.overviewGrid}>
          <div style={styles.overviewItem}>
            <div style={styles.overviewLabel}>Purchase Type</div>
            <div style={styles.overviewValue}>
              {purchaseType === "subscriptionuser" ? "Subscription User" : "Purchase User"}
            </div>
          </div>
          <div style={styles.overviewItem}>
            <div style={styles.overviewLabel}>Subscription Plan</div>
            <div style={styles.overviewValue}>
              {purchaseType === "subscriptionuser"
                ? usersubscriptionplans?.[0]?.planName || "-"
                : "Not applicable"}
            </div>
          </div>
          {purchaseType === "subscriptionuser" && usersubscriptionplans?.[0] && (
            <>
              <div style={styles.overviewItem}>
                <div style={styles.overviewLabel}>subscriptionPlanId</div>
                <div style={styles.overviewValue}>
                  {String(
                    usersubscriptionplans[0].subscriptionPlanId ||
                      usersubscriptionplans[0].planId ||
                      "-"
                  )}
                </div>
              </div>
              <div style={styles.overviewItem}>
                <div style={styles.overviewLabel}>Snapshot status</div>
                <div style={styles.overviewValue}>pending (until contract + payment)</div>
              </div>
            </>
          )}
          <div style={styles.overviewItem}>
            <div style={styles.overviewLabel}>Allow app on web</div>
            <div style={styles.overviewValue}>{allowUserAppOnWeb ? "Yes" : "No"}</div>
          </div>
        </div>
      </div>

      {purchaseType === "subscriptionuser" && (
        <div style={styles.overviewSection}>
          <div style={styles.overviewSectionTitle}>Billing profile</div>
          <div style={styles.overviewGrid}>
            <div style={styles.overviewItem}>
              <div style={styles.overviewLabel}>GSTIN</div>
              <div style={styles.overviewValue}>{billingGstin || "-"}</div>
            </div>
            <div style={styles.overviewItem}>
              <div style={styles.overviewLabel}>State code</div>
              <div style={styles.overviewValue}>{billingStateCode || "-"}</div>
            </div>
            <div style={styles.overviewItem}>
              <div style={styles.overviewLabel}>Legal name</div>
              <div style={styles.overviewValue}>{billingLegalName || "-"}</div>
            </div>
            <div style={styles.overviewItem}>
              <div style={styles.overviewLabel}>Address</div>
              <div style={styles.overviewValue}>{billingAddress || "-"}</div>
            </div>
          </div>
        </div>
      )}

      <div style={styles.overviewSection}>
        <div style={styles.overviewSectionTitle}>Locations</div>
        {locations.length === 0 ? (
          <div style={styles.overviewHint}>No locations added.</div>
        ) : (
          <div style={styles.locationSummaryList}>
            {locations.map((loc, idx) => (
              <div key={idx} style={styles.locationSummaryCard}>
                <div style={styles.locationSummaryHeader}>
                  <div style={styles.locationSummaryTitle}>
                    Location {idx + 1}: {loc?.name || "-"}
                  </div>
                </div>

                <div style={styles.locationSummaryBody}>
                  <div style={styles.locationSummaryBlock}>
                    <div style={styles.locationSummaryBlockTitle}>Address</div>
                    <div style={styles.locationSummaryText}>
                      {[
                        loc?.address?.street,
                        loc?.address?.town,
                        loc?.address?.city,
                        loc?.address?.state,
                        loc?.address?.pinCode,
                      ]
                        .filter(Boolean)
                        .join(", ") || "-"}
                    </div>
                  </div>

                  <div style={styles.locationSummaryBlock}>
                    <div style={styles.locationSummaryBlockTitle}>Sub-Locations</div>
                    <div style={styles.locationSummaryChips}>
                      {loc?.subLocations?.length ? (
                        loc.subLocations.map((s, sIdx) => (
                          <span key={sIdx} style={styles.chip}>
                            {s?.name || "-"}
                          </span>
                        ))
                      ) : (
                        <span style={styles.locationSummaryText}>-</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderStep = () => {
    if (activeStep === 0) return renderContactStep();
    if (activeStep === 1) return renderPrimaryContactStep();
    if (activeStep === 2) return renderPurchaseStep();
    if (activeStep === 3) return renderLocationStep();
    return renderOverviewStep();
  };

  return (
    <div style={styles.container}>
      <div style={styles.shell}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <IconButton onClick={handleBack} style={styles.backButton}>
              <ArrowBackIcon />
            </IconButton>
            <div>
              <div style={styles.kicker}>Create User</div>
              <div style={styles.titleRow}>
                <h2 style={styles.title}>User Registration</h2>
              </div>
              <div style={styles.subtitleSmall}>
                Complete each section and review everything on the Overview page before registering.
              </div>
            </div>
          </div>
        </div>

        <div style={styles.stepperWrap}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>

        <div style={styles.content}>
          <div style={styles.form}>{renderStep()}</div>
        </div>

        <div style={styles.footerBar}>
          <div style={styles.footerLeft}>
            {activeStep === steps.length - 1 ? (
              <Button variant="outlined" onClick={handleBack} disabled={isSubmitting}>
                Cancel
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={handlePrevious}
                disabled={isSubmitting || activeStep === 0}
              >
                Previous
              </Button>
            )}
          </div>

          <div style={styles.footerRight}>
            {activeStep < steps.length - 1 ? (
              <Button variant="contained" onClick={handleNext} disabled={isSubmitting} style={styles.primaryCta}>
                Next
              </Button>
            ) : (
              <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting} style={styles.primaryCta}>
                {isSubmitting ? "Registering..." : "Register User"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {snackbarOpen && (
        <div
          style={{
            ...styles.snackbar,
            backgroundColor: snackbarIsError ? "#f44336" : "#4CAF50",
          }}
          onClick={handleCloseSnackbar}
        >
          {message}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    padding: "28px 24px 88px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  shell: {
    maxWidth: "1100px",
    margin: "0 auto",
  },
  header: {
    paddingBottom: "14px",
    borderBottom: "1px solid rgba(148, 163, 184, 0.35)",
    marginBottom: "18px",
  },
  backButton: {
    backgroundColor: "#ffffff",
    border: "1px solid #eef0f3",
    borderRadius: "12px",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  kicker: {
    fontSize: "12px",
    fontWeight: "800",
    letterSpacing: "0.12em",
    color: "#64748b",
    textTransform: "uppercase",
    marginBottom: "6px",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },
  title: {
    color: "#0f172a",
    margin: 0,
    fontSize: "28px",
    fontWeight: "800",
    letterSpacing: "-0.02em",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: "999px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    color: "#0f172a",
    fontSize: "12px",
    fontWeight: "750",
    whiteSpace: "nowrap",
  },
  subtitleSmall: {
    marginTop: "4px",
    color: "#475569",
    fontSize: "14px",
    lineHeight: 1.4,
  },
  stepperWrap: {
    padding: "14px 14px 10px",
    marginBottom: "18px",
    borderRadius: "16px",
    border: "1px solid rgba(148, 163, 184, 0.25)",
    backgroundColor: "rgba(255, 255, 255, 0.85)",
    boxShadow: "0 10px 30px rgba(2, 6, 23, 0.05)",
  },
  content: {
    paddingTop: "2px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  footerBar: {
    position: "sticky",
    bottom: 0,
    marginTop: "22px",
    padding: "14px 0",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(10px)",
    borderTop: "1px solid #eef0f3",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
  },
  footerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  footerRight: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  fieldset: {
    border: "1px solid rgba(148, 163, 184, 0.28)",
    borderRadius: "16px",
    padding: "18px",
    marginBottom: "0px",
    backgroundColor: "rgba(255, 255, 255, 0.92)",
    boxShadow: "0 12px 32px rgba(2, 6, 23, 0.06)",
  },
  accentContact: {
    borderTop: "4px solid #2563eb",
  },
  accentPrimary: {
    borderTop: "4px solid #7c3aed",
  },
  accentPurchase: {
    borderTop: "4px solid #16a34a",
  },
  accentLocation: {
    borderTop: "4px solid #f59e0b",
  },
  legend: {
    padding: "0 10px",
    color: "#0f172a",
    fontWeight: "800",
    fontSize: "14px",
  },
  formGroup: {
    marginBottom: "15px",
  },
  label: {
    display: "block",
    marginBottom: "5px",
    fontSize: "14px",
    color: "#334155",
    fontWeight: "700",
  },
  input: {
    width: "100%",
    padding: "12px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    transition: "border-color 0.2s, box-shadow 0.2s",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
  },
  passwordContainer: {
    display: "flex",
    gap: "10px",
  },
  passwordInput: {
    flex: 1,
    padding: "12px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    backgroundColor: "#ffffff",
  },
  showPasswordButton: {
    padding: "0 15px",
    backgroundColor: "#f1f5f9",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "800",
    transition: "background-color 0.3s",
  },
  error: {
    color: "#e74c3c",
    fontSize: "12px",
    marginTop: "5px",
    display: "block",
  },
  locationCard: {
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    padding: "15px",
    marginBottom: "20px",
    border: "1px solid #eee",
  },
  addressSection: {
    margin: "15px 0",
    padding: "15px",
    backgroundColor: "#f0f0f0",
    borderRadius: "6px",
  },
  subLocationsSection: {
    margin: "15px 0",
  },
  subtitle: {
    color: "#2c3e50",
    marginBottom: "15px",
    fontSize: "15px",
  },
  subLocationGroup: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "10px",
  },
  addButton: {
    padding: "10px 15px",
    backgroundColor: "#16a34a",
    color: "white",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "800",
    transition: "background-color 0.3s",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  removeButton: {
    padding: "10px 15px",
    backgroundColor: "#f8f8ff",
    color: "#e74c3c",
    border: "1px solid #e74c3c",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.3s",
    "&:hover": {
      backgroundColor: "#ffecec",
    },
  },
  removeLocationButton: {
    padding: "10px 15px",
    backgroundColor: "#f8f8ff",
    color: "#e74c3c",
    border: "1px solid #e74c3c",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.3s",
    marginTop: "10px",
    "&:hover": {
      backgroundColor: "#ffecec",
    },
  },
  submitButton: {
    width: "100%",
    padding: "14px",
    backgroundColor: "#3498db",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.3s",
    marginTop: "10px",
    "&:hover": {
      backgroundColor: "#2980b9",
    },
  },
  disabledButton: {
    backgroundColor: "#bdc3c7",
    cursor: "not-allowed",
    "&:hover": {
      backgroundColor: "#bdc3c7",
    },
  },
  snackbar: {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    color: "white",
    padding: "12px 20px",
    borderRadius: "6px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    animation: "fadeIn 0.3s ease-in-out",
    cursor: "pointer",
  },
  subscriptionPlanCard: {
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "15px",
    border: "1px solid #e9ecef",
    position: "relative",
  },
  subscriptionPlanTitle: {
    color: "#2c3e50",
    marginBottom: "15px",
    fontSize: "16px",
    fontWeight: "600",
  },
  billingSection: {
    marginTop: "24px",
    paddingTop: "16px",
    borderTop: "1px solid #e9ecef",
  },
  planPreview: {
    marginBottom: "12px",
    padding: "10px 12px",
    backgroundColor: "#f0fdf4",
    borderRadius: "8px",
    border: "1px solid #bbf7d0",
  },
  planPricing: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    marginBottom: "10px",
    fontSize: "14px",
    color: "#166534",
  },
  planPricingFinal: {
    fontWeight: "600",
    fontSize: "15px",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    color: "#334155",
    cursor: "pointer",
  },
  infoText: {
    color: "#6c757d",
    fontSize: "14px",
    fontStyle: "italic",
    margin: "10px 0",
  },
  overviewCard: {
    backgroundColor: "#ffffff",
    border: "1px solid #eef0f3",
    borderRadius: "14px",
    padding: "18px",
  },
  overviewTitle: {
    margin: 0,
    color: "#2c3e50",
    fontSize: "18px",
    fontWeight: "650",
  },
  divider: {
    margin: "12px 0",
  },
  previewJson: {
    backgroundColor: "#0b1220",
    color: "#e6edf3",
    padding: "14px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "12px",
    lineHeight: 1.45,
    margin: 0,
  },
  overviewHint: {
    marginTop: "10px",
    color: "#6c757d",
    fontSize: "13px",
  },
  overviewSection: {
    marginTop: "16px",
  },
  overviewSectionTitle: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#2c3e50",
    marginBottom: "10px",
  },
  overviewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "12px",
  },
  overviewItem: {
    border: "1px solid #eef0f3",
    borderRadius: "10px",
    padding: "12px",
    backgroundColor: "#fbfcfe",
  },
  overviewLabel: {
    fontSize: "12px",
    color: "#6c757d",
    marginBottom: "6px",
    fontWeight: "600",
  },
  overviewValue: {
    fontSize: "14px",
    color: "#1f2d3d",
    fontWeight: "600",
    wordBreak: "break-word",
  },
  locationSummaryList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  locationSummaryCard: {
    border: "1px solid #eef0f3",
    borderRadius: "12px",
    overflow: "hidden",
    backgroundColor: "#ffffff",
  },
  locationSummaryHeader: {
    padding: "12px 14px",
    backgroundColor: "#f6f8fb",
    borderBottom: "1px solid #eef0f3",
  },
  locationSummaryTitle: {
    fontSize: "14px",
    fontWeight: "750",
    color: "#2c3e50",
  },
  locationSummaryBody: {
    padding: "12px 14px",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "12px",
  },
  locationSummaryBlock: {
    border: "1px solid #f0f2f5",
    borderRadius: "10px",
    padding: "10px",
    backgroundColor: "#fbfcfe",
  },
  locationSummaryBlockTitle: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#6c757d",
    marginBottom: "8px",
  },
  locationSummaryText: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#1f2d3d",
    lineHeight: 1.4,
    wordBreak: "break-word",
  },
  locationSummaryChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: "999px",
    border: "1px solid #e3e8ef",
    backgroundColor: "#ffffff",
    fontSize: "12px",
    fontWeight: "700",
    color: "#2c3e50",
  },
  primaryCta: {
    backgroundImage: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 55%, #0ea5e9 110%)",
    fontWeight: "800",
    textTransform: "none",
  },
};
